import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { SharedService } from '@shared/services/shared.service';
import { IAddress } from 'app/models/address/address';
import { IAddressType } from 'app/models/address/address-type';
import { AddressService } from 'app/services/address/address.service';
@Component({
  selector: 'address',
  templateUrl: './address.component.html',
  styleUrls: ['./address.component.scss']
})
export class AddressComponent implements OnInit, OnDestroy {
  @Input() entityId: number = 0;
  @Input() entityType: string = '';
  @Input() limitEntries: boolean = false;
  @Input() limitTo: number = 2;
  @Input() canEdit: boolean = false;
  @Input() isRenewal: boolean = false;
  @Output() updatedPhysicalAddress: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() reportDistance: EventEmitter<number> = new EventEmitter<number>();
  @Output() reportOutOfState: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() addressesLoaded: EventEmitter<boolean> = new EventEmitter<boolean>();

  public addressTypes: IAddressType[] = [];
  public addresses: IAddress[] = [];
  public address: IAddress = {
    id: 0,
    state: '',
    city: '',
    street: '',
    zip: '',
    county: '',
    archived: false,
    parentId: 0,
    addressTypes: []
  }
  public includeArchived: boolean = false;
  public addingAddress: boolean = false;
  public addressForm = new FormGroup({
    state: new FormControl('', Validators.required),
    city: new FormControl('', Validators.required),
    street: new FormControl('', Validators.required),
    zip: new FormControl('', [Validators.required, Validators.pattern(/^\d{5}(-\d{4})?$/)]),
    county: new FormControl('', [Validators.required]),
    types: new FormControl<IAddressType[] | null>([], Validators.required)
  });

  public addressColumns: string[] = ['street', 'city', 'state', 'zip', 'county', 'types'];
  public addressSource = new MatTableDataSource<IAddress>(this.addresses);
  @ViewChild('addressPaginator', { static: false }) addressPaginator!: MatPaginator;
  @ViewChild('addressSort', { static: false }) addressSort!: MatSort;

  public sharedService = inject(SharedService);
  public addressService = inject(AddressService);

  ngOnInit(): void {
    this.getAddresses(this.includeArchived);
    this.getAddressTypes();
    if(this.isRenewal && this.entityType == this.sharedService.hospitalLicense){
      this.addressForm.get('county')?.disable();
    }
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  cleanup(): void {
    if (this.addingAddress) {
      if (this.address.id > 0) {
        this.saveAddress();
      }
      else if (this.addressForm.dirty && this.addressForm.valid) {
        this.createAddress();
      }
    }
  }

  getAddresses(includeArchived: boolean): void {
    this.includeArchived = includeArchived;
    this.addressService.getAddresses(this.entityId, this.entityType, includeArchived).subscribe({
      next: response => this.addresses = response,
      error: e => {
        this.sharedService.toastr.error("Could not get addresses");
        console.log('Get addresses error', e);
      },
      complete: () => {
        this.updateAddressTable();
        this.reportOutOfStateAddress();
        if (this.addresses.length === 0) {
          this.toggleAddressForm(true);
        }
        this.addressesLoaded.emit(true);
      }
    });
  }

  getAddressTypes(): void {
    this.addressService.getAddressTypes(false).subscribe({
      next: response => this.addressTypes = response,
      error: e => {
        this.sharedService.toastr.error("Could not get address types");
        console.log('Get address types error', e)
      }
    });
  }

  updateAddressTable(): void {
    this.addressSource.data = [...this.addresses]; // Create new array reference

    setTimeout(() => {
      this.addressSource.data = this.addresses;
      this.addressSource.sort = this.addressSort;
      this.addressSource.paginator = this.addressPaginator;
    }, 500);
  }

  getPhysicalAddress(addresses: IAddress[]): IAddress | null {
    return addresses.find(a => !a.archived && a.addressTypes.find(t => t.type === 'Physical')) || null;
  }

  getMailingAddress(addresses: IAddress[]): IAddress | null {
    return addresses.find(a => !a.archived && a.addressTypes.find(t => t.type === 'Mailing')) || null;
  }

  hasPhysicalType(address: IAddress): boolean {
    return address.addressTypes.some(t => t.type === 'Physical');
  }

  reportOutOfStateAddress(): void {
    this.reportOutOfState.emit(this.addresses.some(a => !a.archived && a.state !== 'OR' && a.addressTypes.find(t => t.type === 'Physical')));
  }

  getEmptyAddress(): IAddress {
    let emptyAddress: IAddress = {
      id: 0,
      state: '',
      city: '',
      street: '',
      zip: '',
      county: '',
      archived: false,
      parentId: 0,
      addressTypes: []
    }
    return emptyAddress;
  }

  updateAddressForm(address: IAddress): void {
    this.addressForm.reset({
      state: address.state,
      city: address.city,
      street: address.street,
      zip: address.zip,
      county: address.county,
      types: address.addressTypes
    });
  }

  updateAddress(): void {
    let form = this.addressForm.getRawValue();
    this.address.id = this.address.id;
    this.address.state = form.state ?? '';
    this.address.city = form.city ?? '';
    this.address.street = form.street ?? '';
    this.address.zip = form.zip ?? '';
    this.address.county = form.county ?? '';
    this.address.archived = this.address.archived;
    this.address.parentId = this.entityId;
    this.address.addressTypes = form.types ?? [];
  }

  compareFn(type1: IAddressType, type2: IAddressType): boolean {
    return type1.id === type2.id;
  }

  toggleAddressForm(adding: boolean): void {
    this.addingAddress = adding;
    this.updateAddressForm(this.getEmptyAddress());
    if (!adding) {
      this.updateAddressTable();
      this.address = this.getEmptyAddress();
    }
  }

  editAddress(address: IAddress): void {
    this.addingAddress = true;
    this.address = address;
    this.updateAddressForm(address);
  }

  autoSave(): void {
    if (this.addressForm.valid) {
      if (this.address.id == 0) {
        this.createAddress();
      }
    }
  }

  createAddress(): void {
    if (this.limitEntries && this.addresses.length > this.limitTo) {
      return;
    }
    this.updateAddress();
    this.addressService.createAddress(this.address, this.entityId, this.entityType).subscribe({
      next: response => this.addresses.push(response),
      error: e => {
        this.sharedService.toastr.error("Could not create address");
        console.log('create address error', e);
      },
      complete: () => {
        if (this.address.addressTypes.find(t => t.type === 'Physical')) {
          this.updatedPhysicalAddress.emit(true);
          if (this.address.state !== 'OR') {
            this.reportOutOfState.emit(true)
          };
        }
        this.addingAddress = false;
        this.updateAddressTable();
      }
    });
  }

  saveAddress(): void {
    this.updateAddress();
    this.addressService.updateAddress(this.address).subscribe({
      next: () => {
        let index: number = this.addresses.findIndex(a => a.id == this.address.id);
        this.addresses[index] = this.address;
      },
      error: e => {
        this.sharedService.toastr.error("Could not update address");
        console.log('error updating address', e);
      },
      complete: () => {
        if (this.address.addressTypes.find(t => t.type === 'Physical')) {
          this.updatedPhysicalAddress.emit(true);
          if (this.address.state !== 'OR') {
            this.reportOutOfState.emit(true)
          }
          else {
            this.reportOutOfState.emit(false);
          }
        }
        this.address = this.getEmptyAddress();
        this.toggleAddressForm(false);
      }
    });
  }

  archiveAddress(address: IAddress, archive: boolean): void {
    let archiving: string = archive ? 'Archive ' : 'Un-Archive ';
    this.sharedService.openConfirm(archiving + 'this address?');
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if (confirmed) {
          this.addressService.archiveAddress(address.id, archive).subscribe({
            complete: () => {
              address.archived = archive;
              if (archive) {
                this.addresses = this.addresses.filter(a => a.id !== address.id);
              }
              this.toggleAddressForm(false);
            },
            error: e => {
              this.sharedService.toastr.error('Could not archive address');
              console.log('Archive address error', e);
            }
          });
        }
      }
    );
  }

  deleteAddress(address: IAddress): void {
    this.sharedService.openConfirm('Delete the ' + address.street + ' address?');
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if (confirmed) {
          this.addressService.deleteAddress(address.id).subscribe({
            next: () => {
              // Remove from local array
              this.addresses = this.addresses.filter(a => a.id !== address.id);

              // Update the table data source immediately
              this.updateAddressTable();

              // Close the form
              this.toggleAddressForm(false);

              // Show add form if no addresses left
              if (this.addresses.length === 0) {
                this.toggleAddressForm(true);
              }

              // Report physical address changes if applicable
              if (address.addressTypes.find(t => t.type === 'Physical')) {
                this.updatedPhysicalAddress.emit(true);
                this.reportOutOfStateAddress();
              }

              this.sharedService.toastr.success('Address deleted successfully');
            },
            error: e => {
              this.sharedService.toastr.error('Could not delete address');
              console.log('Delete address error', e);
            }
          });
        }
      }
    );
  }

  getDistanceBetweenAddressess(fromAddress: IAddress, toAddress: IAddress): void {
    this.addressService.getDistanceBetweenAddresses(fromAddress, toAddress).subscribe({
      next: response => this.reportCalculatedDistance(response),
      error: e => {
        console.log('error getting distance', e);
        this.sharedService.toastr.error('Error getting distance');
      }
    });
  }

  private reportCalculatedDistance(response: number): void {
    if (response === 0) {
      this.sharedService.openNotice(`The distance between the parent and subunit could not be calculated.
        You may still submit your application and the distance will be determined by the department.
        If the distance is fewer than 60 miles your application will be denied.`);
    }
    this.reportDistance.emit(response);
  }

}
